import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BookingModal from './BookingModal';
import AuthModals from './AuthModals';
import '../styles/LegalPages.css';

const PrivacyPolicy: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  return (
    <div className="legal-page">
      <Header 
        onOpenLogin={() => setShowLogin(true)}
        onOpenSignup={() => setShowSignup(true)}
      />
      
      {/* Hero Banner */}
      <section className="legal-banner">
        <div className="banner-overlay"></div>
        <div className="banner-content">
          <h1 className="banner-title">Privacy Policy</h1>
          <p className="banner-subtitle">
            Your privacy is important to us. Learn how we protect and handle your personal information.
          </p>
          <div className="banner-icons">
            <div className="banner-icon">
              <i className="fas fa-shield-alt"></i>
              <span>Secure</span>
            </div>
            <div className="banner-icon">
              <i className="fas fa-lock"></i>
              <span>Protected</span>
            </div>
            <div className="banner-icon">
              <i className="fas fa-user-check"></i>
              <span>Trusted</span>
            </div>
          </div>
        </div>
      </section>

      <div className="legal-page-container">        

        <div className="legal-content">
          <section>
            <h2>1. Information We Collect</h2>
            <p>WeLegal collects information you provide directly to us, such as when you create an account, book a consultation, or contact us for support.</p>
            
            <h3>Personal Information</h3>
            <ul>
              <li>Name, email address, and phone number</li>
              <li>Date of birth and gender (optional)</li>
              <li>Address and location information</li>
              <li>Payment information (processed securely through third-party providers)</li>
              <li>Legal consultation details and case information</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage data (pages visited, time spent, click patterns)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and maintain our legal consultation services</li>
              <li>Process bookings and facilitate lawyer-client communications</li>
              <li>Send service updates, confirmations, and important notifications</li>
              <li>Improve our platform and develop new features</li>
              <li>Comply with legal obligations and protect our rights</li>
              <li>Prevent fraud and ensure platform security</li>
            </ul>
          </section>

          <section>
            <h2>3. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:</p>
            
            <h3>Service Providers</h3>
            <p>We may share information with trusted third-party service providers who assist us in operating our platform, conducting business, or serving our users.</p>
            
            <h3>Legal Requirements</h3>
            <p>We may disclose your information when required by law, court order, or government request.</p>
            
            <h3>Legal Professionals</h3>
            <p>Your consultation details are shared with assigned lawyers to provide legal services. All lawyers are bound by professional confidentiality obligations.</p>
          </section>

          <section>
            <h2>4. Data Security</h2>
            <p>We implement industry-standard security measures to protect your personal information:</p>
            <ul>
              <li>SSL encryption for data transmission</li>
              <li>Secure data storage with encrypted databases</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication systems</li>
              <li>Employee training on data protection</li>
            </ul>
          </section>

          <section>
            <h2>5. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request copies of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Receive your data in a structured format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            </ul>
          </section>

          <section>
            <h2>6. Cookies and Tracking</h2>
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Analyze website usage and performance</li>
              <li>Provide personalized experiences</li>
              <li>Ensure platform security</li>
            </ul>
            <p>You can control cookie settings through your browser preferences.</p>
          </section>

          <section>
            <h2>7. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to:</p>
            <ul>
              <li>Provide our services to you</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Improve our services</li>
            </ul>
            <p>Consultation records are retained for 7 years as required by legal professional standards.</p>
          </section>

          <section>
            <h2>8. Children's Privacy</h2>
            <p>Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18.</p>
          </section>

          <section>
            <h2>9. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.</p>
          </section>

          <section>
            <h2>10. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> privacy@welegal.co</p>
              <p><strong>Phone:</strong> +91 9999999999</p>
              <p><strong>Address:</strong> WeLegal Legal Services, Delhi NCR, India</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
      
      <BookingModal 
        isOpen={showBooking}
        onClose={() => setShowBooking(false)}
      />
      <AuthModals 
        showLogin={showLogin}
        showSignup={showSignup}
        onCloseLogin={() => setShowLogin(false)}
        onCloseSignup={() => setShowSignup(false)}
        onOpenSignup={() => setShowSignup(true)}
        onOpenLogin={() => setShowLogin(true)}
      />
    </div>
  );
};

export default PrivacyPolicy;